import React, { useState, useEffect, useCallback } from "react";
import Avatar from "../../components/ui/Avatar";
import PageWrapper from "../../components/layout/PageWrapper";
import useToast from "../../hooks/useToast";
import Button from "../../components/forms/Button";

import { attendanceService } from "../../api/attendanceService";
import {
  Clock,
  LogIn,
  LogOut,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Briefcase,
  MapPin,
  Users,
  Search,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  RefreshCw,
  WifiOff,
} from "lucide-react";

const STATUS_CONFIG = {
  Working: {
    color: "text-blue-600 dark:text-blue-400",
    bg: "bg-blue-100 dark:bg-blue-900/30",
    icon: Briefcase,
  },
  Present: {
    color: "text-green-600 dark:text-green-400",
    bg: "bg-green-100 dark:bg-green-900/30",
    icon: CheckCircle2,
  },
  WFH: {
    color: "text-purple-600 dark:text-purple-400",
    bg: "bg-purple-100 dark:bg-purple-900/30",
    icon: MapPin,
  },
  "No Check-out": {
    color: "text-amber-600 dark:text-amber-400",
    bg: "bg-amber-100 dark:bg-amber-900/30",
    icon: AlertCircle,
  },
  Absent: {
    color: "text-red-600 dark:text-red-400",
    bg: "bg-red-100 dark:bg-red-900/30",
    icon: XCircle,
  },
  Inactive: {
    color: "text-orange-600 dark:text-orange-400",
    bg: "bg-orange-100 dark:bg-orange-900/30",
    icon: WifiOff,
  },
};

const formatTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const [year, month, day] = dateStr.split("-");
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const calcHours = (checkIn, checkOut) => {
  if (!checkIn) return "—";
  const start = new Date(checkIn);
  const end = checkOut ? new Date(checkOut) : new Date();
  const diffMs = end - start;
  const hours = Math.floor(diffMs / 3600000);
  const mins = Math.floor((diffMs % 3600000) / 60000);
  return `${hours}h ${mins}m`;
};

const TEN_MINUTES = 10 * 60 * 1000;

const getDisplayStatus = (rec) => {
  if (rec.status === "Working" && !rec.check_out) {
    if (
      !rec.last_seen ||
      Date.now() - new Date(rec.last_seen).getTime() > TEN_MINUTES
    ) {
      return "Inactive";
    }
  }
  return rec.status;
};

const getTodayStr = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const AdminAttendance = () => {
  const { showToast } = useToast();
  const [records, setRecords] = useState([]);
  const [date, setDate] = useState(getTodayStr());
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);

  const handleResolveCheckouts = async () => {
    try {
      setResolving(true);
      const result = await attendanceService.runForgottenCheckoutResolution();
      const { resolvedPresent = 0, resolvedNoCheckout = 0 } =
        result?.stats || {};
      showToast(
        `Resolved ${resolvedPresent} Present, ${resolvedNoCheckout} No Check-out`,
        "success",
      );
      fetchRecords(page);
    } catch (err) {
      showToast("Failed to resolve forgotten check-outs", "error");
    } finally {
      setResolving(false);
    }
  };

  const fetchRecords = useCallback(
    async (p = 1) => {
      try {
        setLoading(true);
        const data = await attendanceService.getAll({
          date: date || undefined,
          page: p,
          limit: 50,
        });
        setRecords(data.records || []);
        setTotalPages(data.totalPages || 1);
        setTotal(data.total || 0);
        setPage(data.page || 1);
      } catch (err) {
        showToast("Failed to load attendance records", "error");
      } finally {
        setLoading(false);
      }
    },
    [date, showToast],
  );

  useEffect(() => {
    fetchRecords(1);
  }, [fetchRecords]);

  // Filter records by search term (employee name)
  const filteredRecords = records.filter((rec) => {
    if (!searchTerm) return true;
    const emp = rec.user_id;
    if (!emp) return false;
    const name = (emp.fullNameEn || "") + " " + (emp.fullNameAr || "");
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Summary counts
  const workingCount = records.filter((r) => r.status === "Working").length;
  const presentCount = records.filter((r) => r.status === "Present").length;
  const noCheckoutCount = records.filter(
    (r) => r.status === "No Check-out",
  ).length;

  return (
    <PageWrapper
      title="Attendance Management"
      subtitle="View all employee attendance records"
    >
      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-dark-300 flex items-center justify-center">
              <Users className="w-4 h-4 text-gray-600 dark:text-light-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-light-500">Total</p>
              <p className="text-xl font-bold text-gray-900 dark:text-light-100">
                {total}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Briefcase className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-light-500">
                Working
              </p>
              <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                {workingCount}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-light-500">
                Present
              </p>
              <p className="text-xl font-bold text-green-600 dark:text-green-400">
                {presentCount}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-light-500">
                No Check-out
              </p>
              <p className="text-xl font-bold text-amber-600 dark:text-amber-400">
                {noCheckoutCount}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search employee..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-dark-100 border border-gray-200 dark:border-dark-200 rounded-lg text-sm text-gray-900 dark:text-light-100 placeholder-gray-400 focus:ring-2 focus:ring-primary-accent focus:border-transparent outline-none transition-all"
          />
        </div>
        <div className="relative">
          <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="pl-10 pr-4 py-2.5 bg-white dark:bg-dark-100 border border-gray-200 dark:border-dark-200 rounded-lg text-sm text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent outline-none transition-all"
          />
        </div>
        <div className="flex flex-row gap-2 sm:contents">
          <button
            onClick={() => fetchRecords(page)}
            disabled={loading}
            className="flex flex-1 sm:flex-none items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-light-400 border border-gray-200 dark:border-dark-200 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-300 disabled:opacity-40 transition-colors bg-white dark:bg-dark-100"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <Button
            size="sm"
            onClick={handleResolveCheckouts}
            disabled={resolving}
            className="flex-1 sm:flex-none bg-amber-500 hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            <LogOut className={`w-4 h-4 ${resolving ? "animate-spin" : ""}`} />
            <span className="inline sm:hidden">
              {resolving ? "Fixing..." : "Fix Checkouts"}
            </span>
            <span className="hidden sm:inline">
              {resolving ? "Resolving..." : "Resolve Forgotten Check-outs"}
            </span>
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-accent"></div>
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="text-center py-16 text-gray-500 dark:text-light-500">
            No attendance records found
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block md:hidden space-y-3">
              {filteredRecords.map((rec) => {
                const emp = rec.user_id;
                const displayStatus = getDisplayStatus(rec);
                const cfg =
                  STATUS_CONFIG[displayStatus] || STATUS_CONFIG["Working"];
                const RecIcon = cfg.icon;
                return (
                  <div
                    key={rec._id}
                    className="bg-white dark:bg-primary-card rounded-xl border border-gray-100 dark:border-dark-200 shadow-sm p-4"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Avatar src={emp?.photo} name={emp?.fullNameEn || emp?.fullNameAr} size="sm" />
                        <span className="text-sm font-semibold text-gray-900 dark:text-light-100">
                          {emp?.fullNameEn || emp?.fullNameAr || "Unknown"}
                        </span>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}
                      >
                        <RecIcon className="w-3 h-3" />
                        {displayStatus}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-light-500 mb-0.5">
                          Date
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-light-100">
                          {formatDate(rec.date)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-light-500 mb-0.5">
                          Hours
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-light-100">
                          {calcHours(rec.check_in, rec.check_out)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-light-500 mb-0.5">
                          Check In
                        </p>
                        <p className="text-sm font-medium text-green-600 dark:text-green-400">
                          {formatTime(rec.check_in) || "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-light-500 mb-0.5">
                          Check Out
                        </p>
                        <p className="text-sm font-medium text-red-600 dark:text-red-400">
                          {formatTime(rec.check_out) || "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-light-500 mb-0.5">
                          Last Seen
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-light-100">
                          {formatTime(rec.last_seen) || "—"}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-100 to-gray-50 dark:from-dark-400 dark:to-dark-300">
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Employee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Check In
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Check Out
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Hours
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Last Seen
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-primary-card divide-y divide-gray-200 dark:divide-dark-100">
                  {filteredRecords.map((rec) => {
                    const emp = rec.user_id;
                    const displayStatus = getDisplayStatus(rec);
                    const cfg =
                      STATUS_CONFIG[displayStatus] || STATUS_CONFIG["Working"];
                    const RecIcon = cfg.icon;
                    return (
                      <tr
                        key={rec._id}
                        className="hover:bg-gray-50 dark:hover:bg-dark-300 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <Avatar src={emp?.photo} name={emp?.fullNameEn || emp?.fullNameAr} size="sm" className="!w-9 !h-9" />
                            <span className="text-sm font-medium text-gray-900 dark:text-light-100">
                              {emp?.fullNameEn || emp?.fullNameAr || "Unknown"}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-light-300">
                          {formatDate(rec.date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-light-300">
                          <div className="flex items-center gap-1.5">
                            <LogIn className="w-3.5 h-3.5 text-green-500" />
                            {formatTime(rec.check_in)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-light-300">
                          <div className="flex items-center gap-1.5">
                            <LogOut className="w-3.5 h-3.5 text-red-500" />
                            {formatTime(rec.check_out)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}
                          >
                            <RecIcon className="w-3 h-3" />
                            {displayStatus}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-light-300">
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-indigo-500" />
                            {calcHours(rec.check_in, rec.check_out)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-light-300">
                          {formatTime(rec.last_seen)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-100 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-light-500">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => fetchRecords(page - 1)}
                    disabled={page <= 1}
                    className="p-2 rounded-lg border border-gray-200 dark:border-dark-100 text-gray-600 dark:text-light-400 hover:bg-gray-100 dark:hover:bg-dark-300 disabled:opacity-40 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => fetchRecords(page + 1)}
                    disabled={page >= totalPages}
                    className="p-2 rounded-lg border border-gray-200 dark:border-dark-100 text-gray-600 dark:text-light-400 hover:bg-gray-100 dark:hover:bg-dark-300 disabled:opacity-40 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </PageWrapper>
  );
};

export default AdminAttendance;
